import { IShuttleAddedJourney, IShuttleStopOption } from "../../Orchestrator/Types/Interface/IShuttleBus";
import { dubaiDate } from "./shuttleTime";
import { RideHailingMode } from "../../Orchestrator/Types/Interface/IRideHailing";

const SHUTTLE_SUBSTATION_TYPE = "bus";

interface MinimalSubStation {
    id: string;
    name?: string;
    type?: string;
}

interface MinimalStation {
    id: string;
    name?: string;
    cityName?: string;
    subStations?: MinimalSubStation[] | null;
}

/**
 * Shuttle stops for a station = that station's `subStations` of type "bus".
 * `stationId` is the train station's stopPlaceRef (== the station entry's `id`, the
 * externalCodes-mapped code). Each returned `stopId` is the shuttle's mapped code,
 * which is used directly as the offers `stopPlaceRef`.
 */
export function getShuttleStopOptions(
    stations: MinimalStation[],
    stationId: string
): IShuttleStopOption[] {
    if (!stationId) return [];
    const station = (stations || []).find((s) => s.id === stationId);
    if (!station || !Array.isArray(station.subStations)) return [];
    return station.subStations
        .filter((sub) => (sub?.type || "").toLowerCase() === SHUTTLE_SUBSTATION_TYPE)
        .map((sub) => ({ stopId: sub.id, stopName: sub.name || sub.id }));
}

export function hasShuttleStops(stations: MinimalStation[], stationId: string): boolean {
    return getShuttleStopOptions(stations, stationId).length > 0;
}

export interface NormalizedBookedShuttle {
    offerId: string;
    stopId: string;
    originRef: string;
    destinationRef: string;
    /** Localized name of the offer's origin endpoint. */
    originName: string;
    /** Localized name of the offer's destination endpoint. */
    destinationName: string;
    /** Non-station endpoint name (kept for back-compat / simple callers). */
    stopName: string;
    /** Station endpoint name (kept for back-compat / simple callers). */
    stationName: string;
    depTime: string;
    arrTime: string;
    /** Full ISO departure, kept so callers can compare the shuttle's DATE. */
    depDateTime?: string;
    trainNumber: string;
    durationLabel: string;
    totalPrice: number;
    passengerCount: number;
    /** Riders' externalRefs — pre-checks the passenger list on edit. */
    passengerRefs?: string[];
}

/**
 * Every booked shuttle per side, in booking order — the un-collapsed form of
 * [[mapBookedShuttlesToSides]].
 *
 * A side legitimately holds more than one offer once a shuttle can be booked
 * for the passengers an earlier one left out (3 riders, 2 covered, the third
 * buys their own). Collapsing to one there hides a PAID product: the card
 * cannot draw it and the refund path cannot reach it, because everything
 * downstream is keyed on the single offerId that won the side.
 *
 * Offers on the SAME departure are merged into one card, as the mobile app's
 * `mergeSameTrip` does: two purchases on one bus drawn as two identical rows
 * read as a duplicate booking (reported live). The merged entry keeps the first
 * offer's identity and records its siblings in `extraOfferIds`.
 *
 * The cost, and the thing every consumer must honour: a merged card is refunded
 * and deleted as a UNIT. `shuttleFulfillmentIds` and
 * `shuttleRefundablePassengerIds` therefore take one id or a list, and
 * `removeOnwardShuttle` loops every id. Naming only the first leaves the other
 * half paid for and unreachable.
 */
export function mapBookedShuttlesToSideLists(
    bookedShuttles: NormalizedBookedShuttle[],
    trainOriginRef: string,
    trainDestinationRef: string,
    belongsToLeg?: (shuttle: NormalizedBookedShuttle, side: RideHailingMode) => boolean
): Partial<Record<RideHailingMode, IShuttleAddedJourney[]>> {
    const result: Partial<Record<RideHailingMode, IShuttleAddedJourney[]>> = {};
    for (const s of bookedShuttles || []) {
        const isToStation = s.destinationRef === trainOriginRef;
        const isFromStation = s.originRef === trainDestinationRef;
        const side: RideHailingMode | null = isToStation ? "toStation" : isFromStation ? "fromStation" : null;
        if (!side) continue;
        if (belongsToLeg && !belongsToLeg(s, side)) continue;
        // toStation: stop = offer origin, station = offer destination (the train's origin station).
        // fromStation: station = offer origin (the train's destination station), stop = offer destination.
        const stopName = isToStation ? (s.originName || s.stopName) : (s.destinationName || s.stopName);
        const stationName = isToStation ? (s.destinationName || s.stationName) : (s.originName || s.stationName);
        const entry: IShuttleAddedJourney = {
            side,
            offerId: s.offerId,
            stopId: side === "toStation" ? s.originRef : s.destinationRef,
            stopName,
            stationName,
            depTime: s.depTime,
            arrTime: s.arrTime,
            depDateTime: s.depDateTime,
            trainNumber: s.trainNumber,
            durationLabel: s.durationLabel,
            totalPrice: s.totalPrice,
            passengerCount: s.passengerCount,
            passengerRefs: s.passengerRefs,
        };
        const bucket = (result[side] ||= []);
        // Two purchases on ONE bus are one card. Same stop, same departure, same
        // Dubai day — the identity the accompaniment rule already uses.
        // trainNumber is deliberately not part of it: the booking's service
        // label and the offers list disagree often enough that requiring it
        // would split a card that is genuinely one departure.
        // An entry with no stop or no times is UNREADABLE, not "matching another
        // unreadable one" — the same judgement uncoveredShuttleRefs makes when it
        // refuses rather than guess. Merging those would put two offers behind one
        // Refund button on no evidence they are the same bus.
        const isReadable = !!entry.stopId && !!entry.depTime && !!entry.arrTime;
        const sameDeparture = !isReadable ? undefined : bucket.find(
            (e) =>
                !!e.stopId &&
                e.stopId === entry.stopId &&
                e.depTime === entry.depTime &&
                e.arrTime === entry.arrTime &&
                sameDubaiDay(e.depDateTime, entry.depDateTime)
        );
        if (!sameDeparture) {
            bucket.push(entry);
            continue;
        }
        // Merged in booking order, first offer keeps the identity. Riders are
        // UNIONED rather than concatenated: an admission shared by two offers
        // would otherwise be counted twice and the card would claim a rider more
        // than the bus carries.
        const mergedRefs = [
            ...(sameDeparture.passengerRefs || []),
            ...(entry.passengerRefs || []).filter(
                (ref) => !(sameDeparture.passengerRefs || []).includes(ref)
            ),
        ];
        sameDeparture.extraOfferIds = [...(sameDeparture.extraOfferIds || []), entry.offerId];
        sameDeparture.passengerRefs = mergedRefs;
        sameDeparture.totalPrice = (sameDeparture.totalPrice || 0) + (entry.totalPrice || 0);
        // Counted from the riders when both sides carry them — `passengerCount`
        // can describe an admission rather than a head, and the union above is
        // the more honest number. Falls back to the sum when refs are unreadable.
        sameDeparture.passengerCount =
            mergedRefs.length > 0
                ? mergedRefs.length
                : (sameDeparture.passengerCount || 0) + (entry.passengerCount || 0);
    }
    return result;
}

/**
 * Same Dubai calendar day — STRICT, unlike the modal's `sameShuttleDay`.
 *
 * The two look alike and must not be unified. In the modal a missing date only
 * relaxes a refusal, so permissive fails safe. HERE it decides whether two
 * purchases become ONE CARD, and one card refunds both offers at once — so a
 * missing date must keep them apart. `normalizeBookedShuttle` yields
 * `depDateTime: undefined` with empty times and stopId when its trip lookup
 * misses, which without this folds two unreadable offers into a single row.
 */
function sameDubaiDay(a?: string, b?: string): boolean {
    if (!a || !b) return false;
    return dubaiDate(a) === dubaiDate(b);
}

/**
 * Map booked shuttle offers to journey cards per side, comparing each offer's
 * origin/destination stop ref against the train leg's origin/destination.
 *
 * WARNING for consumers — the list form MERGES same-departure offers, so for
 * such a pair this hands back an entry whose `offerId` names ONE offer while
 * `passengerRefs`, `passengerCount` and `totalPrice` describe BOTH. Anything
 * that acts on the offer id (refund, delete) must take the list form and use
 * `extraOfferIds`; this collapsed form is for display and identity only.
 * `useHydrateBookedShuttles` is the live consumer.
 */
export function mapBookedShuttlesToSides(
    bookedShuttles: NormalizedBookedShuttle[],
    trainOriginRef: string,
    trainDestinationRef: string,
    /**
     * Second discriminator, applied once the side is known and BEFORE the
     * collapse below. Stop refs alone cannot separate two trips on the same
     * route — which is exactly what a partial change-date leaves on the booking
     * — so callers that know the train leg pass the negation of
     * [[isShuttleFromAnotherLegDate]] here to settle it by day.
     *
     * Injected rather than imported: this is a Lib util, and the date rule
     * lives beside the cards in Components/OnwardTravel. Optional so the
     * side-mapping contract every existing caller relies on is unchanged.
     *
     * Must run HERE and not on the result: the collapse is last-in-array-wins,
     * so filtering afterwards lets the other date's offer win the side and then
     * be discarded — the card would show nothing for a shuttle the passenger
     * owns, the same shape of bug `ownedOnly` exists to prevent.
     */
    belongsToLeg?: (shuttle: NormalizedBookedShuttle, side: RideHailingMode) => boolean
): Partial<Record<RideHailingMode, IShuttleAddedJourney>> {
    const lists = mapBookedShuttlesToSideLists(
        bookedShuttles,
        trainOriginRef,
        trainDestinationRef,
        belongsToLeg
    );
    const result: Partial<Record<RideHailingMode, IShuttleAddedJourney>> = {};
    // Last-in-array wins when a side holds two offers, and that is deliberate:
    // nothing in the payload can rank them. Ranking by recency was tried and
    // reverted — `admission.createdOn` and `confirmableUntil` arrive identical
    // on every offer of the booking (verified live on LLUII6, where two shuttles
    // booked 50 minutes apart both read 00:47:04.302727Z), so it is the
    // booking's timestamp, not the offer's.
    //
    // The ambiguous case — a shuttle just refunded, still non-terminal, next to
    // its replacement — is therefore resolved BEFORE this function, by the
    // caller excluding what it refunded (refundedShuttleOfferIdsAtom).
    //
    // Callers that must not LOSE the losers — anything that draws or refunds a
    // paid shuttle — take mapBookedShuttlesToSideLists instead.
    (Object.keys(lists) as RideHailingMode[]).forEach((side) => {
        const entries = lists[side] || [];
        if (entries.length > 0) result[side] = entries[entries.length - 1];
    });
    return result;
}
